/**
 * GNSS Processing Service
 * 
 * Client-side service for communicating with the GNSS backend API
 * Handles job submission, status polling, and results retrieval
 */

import axios, { AxiosProgressEvent } from 'axios';
import { GNSSProcessingConfig, GNSSJobStatus, GNSSProcessingResult } from '../types';

// API Configuration
const API_BASE_URL = process.env.VITE_GNSS_API_URL || 'http://localhost:3000/api/v1/gnss';
const POLL_INTERVAL = 2000; // 2 seconds
const MAX_RETRIES = 3;

interface SubmitJobRequest {
  rinexFile: File;
  referenceFile?: File;
  config: GNSSProcessingConfig;
}

interface SubmitJobResponse {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  estimatedDuration: number;
  statusUrl: string;
}

interface JobStatusResponse {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  resultsUrl?: string;
  error?: string;
}

interface JobResultsResponse {
  jobId: string;
  completedAt: string;
  processingDuration: number;
  statistics: {
    pointsProcessed: number;
    pdop: number;
    horizontalRMS: number;
    verticalRMS: number;
    convergenceTime?: number;
  };
  results: any[];
  downloadUrl?: string;
}

/**
 * Submit a RINEX file for processing
 */
export async function submitGNSSJob(
  request: SubmitJobRequest,
  onProgress?: (progress: number) => void
): Promise<SubmitJobResponse> {
  const formData = new FormData();
  formData.append('rinexFile', request.rinexFile);
  
  if (request.referenceFile) {
    formData.append('referenceFile', request.referenceFile);
  }
  
  formData.append('processingConfig', JSON.stringify(request.config));

  try {
    const response = await axios.post<SubmitJobResponse>(
      `${API_BASE_URL}/jobs`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error?.message || 
      'Failed to submit GNSS job'
    );
  }
}

/**
 * Get job status and progress
 */
export async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  try {
    const response = await axios.get<JobStatusResponse>(
      `${API_BASE_URL}/jobs/${jobId}`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(`Job ${jobId} not found`);
    }
    throw new Error(
      error.response?.data?.error?.message || 
      'Failed to get job status'
    );
  }
}

/**
 * Poll job status until completion
 */
export async function pollJobStatus(
  jobId: string,
  onProgress?: (status: JobStatusResponse) => void,
  maxDuration: number = 3600000 // 1 hour
): Promise<JobStatusResponse> {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await getJobStatus(jobId);
        
        if (onProgress) {
          onProgress(status);
        }

        // Job completed or failed
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(pollInterval);
          resolve(status);
          return;
        }

        // Check timeout
        if (Date.now() - startTime > maxDuration) {
          clearInterval(pollInterval);
          reject(new Error(`Job polling timeout after ${maxDuration}ms`));
          return;
        }
      } catch (error) {
        clearInterval(pollInterval);
        reject(error);
      }
    }, POLL_INTERVAL);
  });
}

/**
 * Get processing results
 */
export async function getJobResults(
  jobId: string,
  format: 'geojson' | 'csv' | 'kml' = 'geojson'
): Promise<JobResultsResponse | string> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/jobs/${jobId}/results`,
      {
        params: { format },
        headers: {
          'Accept': format === 'geojson' ? 'application/json' : 'text/plain',
        },
        responseType: format === 'geojson' ? 'json' : 'text',
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error?.message || 
      'Failed to retrieve job results'
    );
  }
}

/**
 * Download results file
 */
export async function downloadResults(
  jobId: string,
  format: 'csv' | 'kml' = 'csv'
): Promise<Blob> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/jobs/${jobId}/results`,
      {
        params: { format },
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error?.message || 
      'Failed to download results'
    );
  }
}

/**
 * Cancel a job
 */
export async function cancelJob(jobId: string): Promise<void> {
  try {
    await axios.delete(`${API_BASE_URL}/jobs/${jobId}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error?.message || 
      'Failed to cancel job'
    );
  }
}

/**
 * Get API health status
 */
export async function checkAPIHealth(): Promise<boolean> {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`, {
      timeout: 5000,
    });
    return response.status === 200;
  } catch {
    return false;
  }
}

/**
 * Validate RINEX file on server side
 */
export async function validateRINEXFile(file: File): Promise<{
  valid: boolean;
  version?: string;
  fileType?: string;
  system?: string;
  timeOfFirstObs?: string;
  timeOfLastObs?: string;
  interval?: number;
  error?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axios.post(
      `${API_BASE_URL}/validate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      valid: false,
      error: error.response?.data?.error || 'Validation failed',
    };
  }
}

/**
 * Retry a failed job
 */
export async function retryJob(
  originalJobId: string,
  request: SubmitJobRequest
): Promise<SubmitJobResponse> {
  // Submit new job with same parameters
  return submitGNSSJob(request);
}

export default {
  submitGNSSJob,
  getJobStatus,
  pollJobStatus,
  getJobResults,
  downloadResults,
  cancelJob,
  checkAPIHealth,
  validateRINEXFile,
  retryJob,
};
